import { CheckCircle2 as ShoppingCartCheckout, ShoppingCart } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { addProductToCart } from "../../../products/ProductToCart";
import { useDomain } from "../../../../context/DomainContext";
import { useCart } from "../../../../context/CartContext";
import FormatPrice from "../../../../components/FormatPrice";
import { useDispatch } from "react-redux";
import { setSelectedProduct } from "../../../../api/reducers/selectedProductReducer";
import RetailRichTextDescription from "../../RetailRichTextDescription";

const ProductsList = ({ products, loggedIn }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 15;
  const totalPages = Math.ceil(products.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = products.slice(indexOfFirstProduct, indexOfLastProduct);

  const handlePageChange = (direction) => {
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="p-4">
      {/* Product Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6">
        {currentProducts.length > 0 ? (
          currentProducts.map((item) => (
            <ProductItem key={item._id} productItem={item} loggedIn={loggedIn} />
          ))
        ) : (
          <div className="col-span-full">
            <div className="animate-pulse bg-gray-200 h-48 rounded-md"></div>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-center mt-8">
        <button
          onClick={() => handlePageChange("prev")}
          disabled={currentPage === 1}
          className="bg-[#B88E2F] text-white rounded-full px-4 py-2 mx-2 hover:bg-[#A67C2A] transition-colors duration-200"
        >
          Previous
        </button>
        <button
          onClick={() => handlePageChange("next")}
          disabled={currentPage === totalPages}
          className="bg-[#B88E2F] text-white rounded-full px-4 py-2 mx-2 hover:bg-[#A67C2A] transition-colors duration-200"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const ProductItem = ({ productItem }) => {
  const navigate = useNavigate();
  const domain = useDomain();
  const dispatch = useDispatch();
  const { addToCart, updateCart } = useCart();
  const [addedToCart, setAddedToCart] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const viewProductDetail = (product) => {
    dispatch(setSelectedProduct(product));
    navigate("/sf/product-info");
  };

  const handleProductClick = (product) => {
    if (domain === "RETAIL") {
      viewProductDetail(product);
    }
  };

  const handleAddToCart = (product) => {
    addProductToCart({
      addToCart,
      navigate,
      productDetails: product,
      quantity: 1,
      updateCart,
    });

    setAddedToCart(true);
    setSnackbarOpen(true);
    setTimeout(() => {
      setSnackbarOpen(false);
    }, 3000);
  };

  const handleGoToCart = () => {
    navigate("/cart");
  };

  const currentImage = productItem.imageContents?.[0]?.displayLink || productItem.imageURL || "";

  return (
    <div
      className="flex flex-col rounded-lg border border-gray-200 overflow-hidden bg-white shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer"
      onClick={() => handleProductClick(productItem)}
    >
      {/* Product Image */}
      <div className="w-full h-64 relative">
        <img
          loading="lazy"
          src={currentImage}
          alt={productItem.name}
          className="w-full h-full object-cover"
        // onMouseEnter={handleMouseEnter}
        // onMouseLeave={handleMouseLeave}
        />
        {addedToCart && (
          <div className="absolute top-2 right-2 bg-[#B88E2F] text-white px-2 py-1 rounded-full text-xs">
            In Cart
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="p-4 flex flex-col justify-between flex-1">
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-800 hover:text-[#B88E2F] transition-colors duration-200">
            {productItem.name}
          </h2>
          <p className="mt-2 text-xl font-bold text-[#B88E2F]">
            {productItem.price && (
              <FormatPrice
                code={productItem.price.currency || "USD"}
                price={productItem.price.priceAmt}
              />
            )}
          </p>
          <RetailRichTextDescription content={productItem.description} className="mt-2" />
        </div>

        {/* Add to Cart Button */}
        <button
          className={`mt-4 py-2 px-4 rounded-full w-full text-white ${addedToCart ? "bg-gray-600" : "bg-[#B88E2F]"}
            hover:bg-[#A67C2A] transition-colors duration-200 text-sm md:text-base flex items-center justify-center`}
          onClick={(e) => {
            e.stopPropagation();
            if (addedToCart) {
              handleGoToCart();
            } else {
              handleAddToCart(productItem);
            }
          }}
        >
          <span>{addedToCart ? "Go to Cart" : "Add to Cart"}</span>
          <span className="ml-2">
            {addedToCart ? <ShoppingCartCheckout /> : <ShoppingCart />}
          </span>
        </button>
      </div>

      {/* Snackbar Notification */}
      {snackbarOpen && (
        <div
          className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-[#B88E2F] text-white p-2 rounded-md z-50 text-center text-sm md:text-base"
          onClick={() => setSnackbarOpen(false)}
        >
          <span>Added to cart</span>
        </div>
      )}
    </div>
  );
};

export default ProductsList;
